/**
 * Chrome's PDF plugin only handles `application/pdf`. A ZIP extract or File URL
 * often arrives as `application/octet-stream`, and printing that blob dumps the
 * raw `%PDF-1.3…` bytes as text (SSP-4679).
 */
export const asPdfBlob = (blob) => blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
/** Triggers a browser download from a Blob. */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
/** Letter-size box so the PDF plugin has a page to rasterize. */
export const PRINT_PAGE_WIDTH = '8.5in';
export const PRINT_PAGE_HEIGHT = '11in';
/**
 * Empirical wait after the blob `load` for Chrome's PDF plugin to paint.
 * Not a render-complete event — bump only with evidence of a still-blank preview.
 */
export const PRINT_AFTER_LAYOUT_MS = 300;
export const PRINT_CLEANUP_MS = 60000;
/**
 * Opens the PDF in an off-screen iframe and invokes the browser print dialog.
 * Obtaining a paper copy is gated the same way as Download.
 *
 * Chrome fires `load` for the initial `about:blank` document if a src-less
 * iframe is inserted first. Printing that document puts the host page URL in
 * the print footer. Set `src` before insert so the only `load` is the blob.
 * `onload` is still attached before `src` so a synchronous blob cannot miss it.
 *
 * A 0×0 (or `display:none`) PDF iframe is hostile to Chrome's plugin — park a
 * letter-size box off-screen so it has a page to rasterize.
 *
 * Cleanup waits for `afterprint` (fallback starts when `print()` runs) because
 * some engines return from `print()` before the dialog closes.
 */
export function printPdfBlob(blob) {
    const url = URL.createObjectURL(asPdfBlob(blob));
    const frame = document.createElement('iframe');
    frame.setAttribute('data-testid', 'consent-pdf-print-frame');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.position = 'fixed';
    frame.style.top = '0';
    frame.style.left = '-10000px';
    frame.style.width = PRINT_PAGE_WIDTH;
    frame.style.height = PRINT_PAGE_HEIGHT;
    frame.style.border = '0';
    frame.style.margin = '0';
    frame.style.padding = '0';
    let cleaned = false;
    let started = false;
    // DOM 2-arg `window.setTimeout` is `number`. `ReturnType<typeof setTimeout>` is `NodeJS.Timeout` once `@types/node` is in the graph.
    let cleanupTimer;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const cleanup = () => {
        if (cleaned) {
            return;
        }
        cleaned = true;
        if (cleanupTimer !== undefined) {
            window.clearTimeout(cleanupTimer);
        }
        window.removeEventListener('afterprint', cleanup);
        frame.contentWindow?.removeEventListener('afterprint', cleanup);
        previouslyFocused?.focus();
        frame.remove();
        URL.revokeObjectURL(url);
    };
    frame.onload = () => {
        if (started || cleaned) {
            return;
        }
        const win = frame.contentWindow;
        if (!win) {
            cleanup();
            return;
        }
        started = true;
        win.addEventListener('afterprint', cleanup);
        window.addEventListener('afterprint', cleanup);
        window.setTimeout(() => {
            if (cleaned) {
                return;
            }
            win.focus();
            win.print();
            cleanupTimer = window.setTimeout(cleanup, PRINT_CLEANUP_MS);
        }, PRINT_AFTER_LAYOUT_MS);
    };
    frame.src = url;
    document.body.appendChild(frame);
}
